import { Patient } from '../../types';

export type BillingStatus = 'paid' | 'unpaid' | 'processing' | 'denied';

export type BillingRecord = {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: BillingStatus;
  method?: 'insurance' | 'card' | 'cash' | 'check';
};

export type BillingSummary = {
  accountBalance: number;
  lastPayment?: {
    date: string;
    amount: number;
    method: 'insurance' | 'card' | 'cash' | 'check';
  };
  nextDueDate?: string;
  records: BillingRecord[];
};

export default function BillingTab({ patient, billing }: { patient: Patient; billing: BillingSummary }) {
  const getStatusClasses = (status: BillingStatus) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'unpaid':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'denied':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase">Account Balance</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">${billing.accountBalance.toFixed(2)}</p>
          {billing.nextDueDate && (
            <p className="text-sm text-gray-600 mt-2">Next due: {new Date(billing.nextDueDate).toLocaleDateString()}</p>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase">Last Payment</p>
          {billing.lastPayment ? (
            <>
              <p className="text-2xl font-bold text-gray-900 mt-1">${billing.lastPayment.amount.toFixed(2)}</p>
              <p className="text-sm text-gray-600 mt-2">
                {new Date(billing.lastPayment.date).toLocaleDateString()} • {billing.lastPayment.method}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-600 mt-2">No payments recorded</p>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase">Insurance On File</p>
          <p className="text-base font-semibold text-gray-900 mt-1">{patient.insurance.provider}</p>
          <p className="text-sm text-gray-600 mt-1">Policy: {patient.insurance.policyNumber}</p>
          <p className="text-sm text-gray-600">Group: {patient.insurance.groupNumber}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Billing Records</h3>
          <p className="text-sm text-gray-600">Sample data for display</p>
        </div>
        <div className="divide-y divide-gray-200">
          {billing.records.map((record) => (
            <div key={record.id} className="px-6 py-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{record.description}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {new Date(record.date).toLocaleDateString()} • {record.id}
                  {record.method ? ` • ${record.method}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusClasses(record.status)}`}>
                  {record.status}
                </span>
                <p className="text-base font-semibold text-gray-900">${record.amount.toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
